import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone, Sparkles, Star } from "lucide-react";
import { MaskedLine, Crescent, scrollToId, EASE } from "./Reveal";
import { useLang } from "@/i18n";

const HERO_IMG =
    "https://images.unsplash.com/photo-1713085085470-fba013d67e65?q=80&w=1400&auto=format&fit=crop";
const GOOGLE_URL =
    "https://www.google.com/maps/search/?api=1&query=Signita+Wellness+Thane+West+400615";

function Stars() {
    return (
        <span className="flex items-center gap-0.5">
            {[0, 1, 2, 3].map((i) => (
                <Star key={i} className="h-3.5 w-3.5 fill-brand-lime text-brand-lime" />
            ))}
            <span className="relative">
                <Star className="h-3.5 w-3.5 text-slate-300" />
                <span className="absolute inset-0 w-1/2 overflow-hidden">
                    <Star className="h-3.5 w-3.5 fill-brand-lime text-brand-lime" />
                </span>
            </span>
        </span>
    );
}

export default function Hero() {
    const { t } = useLang();
    const [gi, setGi] = useState({ rating: 4.5, reviews: 62 });

    useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/google-info`)
            .then((r) => r.json())
            .then(setGi)
            .catch(() => {});
    }, []);

    const imgWrapRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: imgWrapRef,
        offset: ["start end", "end start"],
    });
    const yImg = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

    return (
        <section data-testid="hero-section" className="relative overflow-hidden pt-32 md:pt-40">
            <Crescent className="animate-spin-slow pointer-events-none absolute -right-40 -top-40 h-[560px] w-[560px] text-brand-olivelight" />
            <div className="pointer-events-none absolute -left-52 top-1/3 h-[420px] w-[420px] rounded-full bg-brand-olivelight/70 blur-3xl" />

            <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-16 md:px-8 lg:grid-cols-12 lg:gap-10 lg:pb-24">
                <div className="lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
                        className="mb-8 flex items-center gap-3"
                    >
                        <span className="relative flex h-2 w-2">
                            <span className="absolute h-2 w-2 animate-ping rounded-full bg-brand-lime opacity-75" />
                            <span className="h-2 w-2 rounded-full bg-brand-lime" />
                        </span>
                        <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-brand-olive md:text-xs">
                            {t.hero.eyebrow}
                        </p>
                    </motion.div>

                    <h1 className="font-display text-[2.7rem] font-semibold leading-[1.06] tracking-tight text-brand-navy sm:text-6xl lg:text-7xl xl:text-[5.4rem]">
                        <MaskedLine delay={0.2}>
                            {t.hero.l1a} <em className="italic text-brand-olive">{t.hero.l1b}</em>
                        </MaskedLine>
                        <MaskedLine delay={0.32}>
                            {t.hero.l2a} <em className="italic text-brand-olive">{t.hero.l2b}</em>
                        </MaskedLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
                        className="mt-7 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg"
                    >
                        {t.hero.sub}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-book-appointment-btn"
                            onClick={() => scrollToId("appointment")}
                            className="group relative inline-flex items-center gap-3 overflow-hidden rounded-full bg-brand-navy px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:shadow-[0_20px_40px_-16px_rgba(13,30,54,0.55)]"
                        >
                            <span className="absolute inset-0 -translate-x-full bg-brand-olive transition-transform duration-500 ease-out group-hover:translate-x-0" />
                            <Sparkles className="relative h-4 w-4" />
                            <span className="relative">{t.hero.cta1}</span>
                        </button>
                        <a
                            data-testid="hero-call-btn"
                            href="tel:+917208991091"
                            className="inline-flex items-center gap-3 rounded-full border border-slate-300 bg-white/70 px-8 py-4 text-sm font-semibold text-brand-navy backdrop-blur transition-all duration-300 hover:border-brand-olive hover:text-brand-olive"
                        >
                            <Phone className="h-4 w-4" />
                            {t.hero.cta2}
                        </a>
                    </motion.div>

                    <motion.a
                        data-testid="google-reviews-badge"
                        href={GOOGLE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
                        className="mt-6 inline-flex items-center gap-3 rounded-full border border-slate-200 bg-white/80 px-5 py-2.5 backdrop-blur transition-all duration-300 hover:border-brand-olive"
                    >
                        <Stars />
                        <span data-testid="google-rating-value" className="text-sm font-bold text-brand-navy">
                            {gi.rating}
                        </span>
                        <span data-testid="google-reviews-count" className="text-xs text-slate-500">
                            {gi.reviews} {t.hero.gReviewsWord}
                        </span>
                    </motion.a>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 0.9 }}
                        className="mt-10 grid max-w-xl grid-cols-3 gap-6 border-t border-slate-200 pt-8"
                    >
                        {t.hero.stats.map((s) => (
                            <div key={s.v}>
                                <p className="font-display text-3xl font-semibold text-brand-navy md:text-4xl">{s.v}</p>
                                <p className="mt-2 text-[11px] leading-snug text-slate-500 md:text-xs">{s.l}</p>
                            </div>
                        ))}
                    </motion.div>
                </div>

                <div className="relative lg:col-span-5">
                    <motion.div
                        initial={{ opacity: 0, y: 40, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 1.1, delay: 0.4, ease: EASE }}
                        className="relative"
                    >
                        <div
                            ref={imgWrapRef}
                            className="relative aspect-[4/5] overflow-hidden rounded-[2rem] border border-slate-200 shadow-lift"
                        >
                            <motion.img
                                src={HERO_IMG}
                                alt="Dermatology consultation at Signita Wellness"
                                style={{ y: yImg, scale: 1.16 }}
                                className="h-full w-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/30 via-transparent to-transparent" />
                        </div>

                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
                            className="absolute -bottom-6 -left-4 flex items-center gap-4 rounded-2xl border border-brand-accentline bg-white/90 p-5 shadow-lift backdrop-blur-xl md:-left-10"
                        >
                            <Crescent className="h-9 w-9 text-brand-lime" />
                            <div>
                                <p className="text-sm font-bold text-brand-navy">{t.hero.badge1t}</p>
                                <p className="text-xs text-slate-500">{t.hero.badge1s}</p>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: -24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
                            className="absolute -top-5 right-6 rounded-2xl bg-brand-navy px-5 py-4 text-white shadow-lift"
                        >
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-lime">{t.hero.badge2t}</p>
                            <p className="mt-1 text-xs font-medium">{t.hero.badge2s}</p>
                        </motion.div>
                    </motion.div>
                </div>
            </div>

            <motion.button
                data-testid="hero-scroll-cue"
                onClick={() => scrollToId("services")}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 1 }}
                className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400 transition-colors hover:text-brand-olive lg:flex"
            >
                {t.hero.scroll}
                <ArrowDown className="h-3.5 w-3.5 animate-bounce" />
            </motion.button>
        </section>
    );
}
